import { Link } from "react-router-dom";
import { Calendar, Users2, ShieldCheck, MessageSquare } from "lucide-react";

const Value = ({ icon: Icon, title, children }) => (
  <div className="flex gap-3 items-start">
    <div className="mt-1 shrink-0 w-9 h-9 rounded-full bg-sage-100 text-sage-700 flex items-center justify-center">
      <Icon size={18} />
    </div>
    <div>
      <h3 className="font-heading font-semibold text-ink-900">{title}</h3>
      <p className="text-ink-700 text-sm leading-relaxed mt-1">{children}</p>
    </div>
  </div>
);

export default function About() {
  return (
    <div className="min-h-screen bg-ivory">
      <div className="max-w-2xl mx-auto px-5 py-10 space-y-8">
        <div>
          <div className="label-overline">About</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mt-2 text-ink-900">
            An honest little diary <span className="italic text-sage-600">for all musicians</span>
          </h1>
        </div>

        <p className="text-ink-800 text-base leading-relaxed">
          AMDS — <strong>All Musicians Diary Service</strong> — is a working diary and
          dep-finding tool for the people at the sharp end of live music: freelance
          musicians, conductors, orchestra managers and ensemble fixers. It is quiet,
          honest, and built to be used every day.
        </p>

        <div className="rounded-2xl bg-sage-100 border border-sage-200 p-5 text-sm text-ink-800">
          <strong>Public beta.</strong> AMDS is early-stage software. Every user is helping
          shape it. Bug reports and suggestions go straight to me — please use the Feedback
          link in the footer of any page.
        </div>

        <div className="card p-6 space-y-5">
          <h2 className="font-heading text-2xl font-semibold text-ink-900">Why AMDS exists</h2>
          <p className="text-ink-700 text-sm leading-relaxed">
            Because most musicians run their lives out of a mixture of paper diaries,
            calendar apps not built for gigs, WhatsApp threads, and spreadsheets. Finding a
            dep for tomorrow shouldn&apos;t require broadcasting into six group chats. Keeping
            an accurate diary shouldn&apos;t mean retyping every event twice.
          </p>
          <p className="text-ink-700 text-sm leading-relaxed">
            AMDS is diary-first: your dates are the source of truth. Availability is
            derived from your diary. Dep-finding uses that availability. Everything else
            (bookings, ensembles, contacts) hangs off the diary too.
          </p>
        </div>

        <div className="space-y-5">
          <Value icon={Calendar} title="Diary-first">
            You keep your diary; AMDS derives availability from it. Bookers see only your
            availability status — never your event details.
          </Value>
          <Value icon={Users2} title="Built for the whole ecosystem">
            Musicians, bookers, conductors, orchestra managers, dep fixers — all first-class
            users, all with role-appropriate views.
          </Value>
          <Value icon={ShieldCheck} title="Trust-first">
            Diary contents are private. Passwords are hashed. Email verification is
            required. AMDS does not sell your data.
          </Value>
          <Value icon={MessageSquare} title="Made openly, in the UK">
            Built by a working musician for working musicians. Every feature comes from a
            real workflow, not a marketing plan.
          </Value>
        </div>

        <div className="card p-6">
          <h2 className="font-heading text-2xl font-semibold text-ink-900 mb-3">Get in touch</h2>
          <p className="text-ink-700 text-sm leading-relaxed">
            The fastest way to reach me is the{" "}
            <Link to="/feedback" className="text-sage-600 underline">Feedback</Link> link in
            the footer of any AMDS page. For anything privacy- or account-specific,
            email <strong>[[support@amdsdiary.org]]</strong>.
          </p>
        </div>

        <p className="text-center text-ink-500 text-xs mt-10">
          <Link to="/" className="hover:text-sage-600">Back to AMDS</Link>
        </p>
      </div>
    </div>
  );
}
